import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { saveAs } from "file-saver";
import apiClient from "../../utils/clients/axios";

// Opened from the image_url links in downloaded reports (ERP MANAGER only)
const TITLES = {
  guest: "Guest Image",
  key: "Key Person Image",
  materialinward: "Material Inward Image",
};

const errorMessage = (status) => {
  if (status === 403) return "Only ERP MANAGER users can view report images.";
  if (status === 404) return "Image not found. The record may have no image or was deleted.";
  return "Could not load the image. Please try again.";
};

function ImageViewer() {
  const { kind, id } = useParams();
  const navigate = useNavigate();
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    setImage(null);
    setError("");

    apiClient
      .get(`/images/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`, {
        responseType: "blob",
      })
      .then(({ data }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setImage({ url: objectUrl, blob: data });
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err?.response?.status));
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [kind, id]);

  const download = () => {
    const extension = (image.blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
    saveAs(image.blob, `${kind}_${id}.${extension}`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        p: 2,
        bgcolor: "#f5f5f5",
      }}
    >
      <Typography variant="h5">{TITLES[kind] || "Image"}</Typography>

      {!image && !error && <CircularProgress sx={{ mt: 4 }} />}

      {error && (
        <Typography color="error" sx={{ mt: 4, textAlign: "center" }}>
          {error}
        </Typography>
      )}

      {image && (
        <>
          <Box
            component="img"
            src={image.url}
            alt={TITLES[kind] || "Report image"}
            sx={{
              maxWidth: "100%",
              maxHeight: "75vh",
              objectFit: "contain",
              border: "1px solid #ddd",
              borderRadius: 1,
              bgcolor: "#fff",
            }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={download}>
              Download
            </Button>
            <Button variant="outlined" onClick={() => navigate("/admin")}>
              Go to dashboard
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

export default ImageViewer;
